import { useMemo } from 'react';
import { RequestsTableResponse, lastMessageBy, requestStatus } from 'src/api/services/RequestClient';

/**
 * Check if a request has unread messages
 *
 * Uses backend fields for accurate tracking:
 * - last_message_at: Timestamp of last message (from anyone)
 * - last_customer_message_at: Timestamp of last customer message
 * - last_message_by: Who sent the last message ("admin", "editor", or "customer")
 * - reviewed_by_editor: Auto-updated by backend when editor fetches request (GET /api/requests/{id})
 *
 * Badge logic:
 * 1. If no messages exist → No badge
 * 2. If last message was from our side (admin/editor/team_lead) → No badge
 *    (admin-sent messages cover re-edit/status-change side effects; editor-sent
 *     messages shouldn't notify the sender on their own message)
 * 3. If never reviewed by editor → Show badge
 * 4. If message is after editor's last review → Show badge
 */
export const useRequestUnreadMessages = () => {
  const hasUnreadMessages = useMemo(() => {
    return (request: RequestsTableResponse): boolean => {
      // Prioritize customer messages, fallback to any message
      const lastMessageAt = request.last_customer_message_at || request.last_message_at;

      // No message exists
      if (!lastMessageAt) return false;

      // Our side sent the last message — don't badge the sender.
      // Covers admin (re-edit/status-change side effects) and editor/team_lead
      // (the person who just posted shouldn't see an unread icon on their own message).
      if (
        request.last_message_by === lastMessageBy.admin ||
        request.last_message_by === lastMessageBy.editor
      ) {
        return false;
      }

      // Queued requests don't have messages yet
      if (request.status === requestStatus.queued) {
        return false;
      }

      try {
        // Editor never reviewed this request → Show badge
        if (!request.reviewed_by_editor) {
          return true;
        }

        // Compare message timestamp with editor's last review
        const lastMessageDate = new Date(lastMessageAt);
        const reviewedDate = new Date(request.reviewed_by_editor);

        // Show badge if message is after editor's last review
        return lastMessageDate > reviewedDate;
      } catch (error) {
        console.error('Error checking unread messages:', error);
        return false;
      }
    };
  }, []);

  return {
    hasUnreadMessages,
  };
};
